import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { TabView, TabPanel } from 'primereact/tabview';
import { Calendar } from 'primereact/calendar';
import { AutoComplete, AutoCompleteCompleteEvent } from 'primereact/autocomplete';
import { Dropdown } from 'primereact/dropdown';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputNumber } from 'primereact/inputnumber';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { MultiSelect } from 'primereact/multiselect';
import { InputText } from 'primereact/inputtext';
import { Toast } from 'primereact/toast';
import { ProgressSpinner } from 'primereact/progressspinner';
import {
    getSurgeryPart,
    getStockItems,
    validateKit,
    getStockBalance,
    getProfessionals,
    getProfessionalRoles,
    getLegacyAnesthesiaOptions,
    getAnesthesiaTypeOptions,
    getAnesthesiaTechniqueOptions,
    getAnesthesiaProcedureOptions,
    getSurgicalRiskOptions,
    saveSurgeryPart,
    getNomenclatorsForHealthEntity,
} from '../../servicesHooks/SurgeryPart/useSurgeryPartService';
import {
    ComboItem,
    SurgeryKitMissingItemDTO,
    SurgeryPartDTO,
    SurgeryPracticeCodeDTO,
    SurgeryProfessionalDTO,
    SurgeryStockItemComboDTO,
    SurgeryStockItemDTO,
} from '../../interface/surgery';
import { toISOString } from '../../utils/localeHelpers';
import SurgeryKitMissingItemsDialog from './SurgeryKitMissingItemsDialog';
import './SurgeryPartView.scss';
import { INSUFFICIENT_STOCK, parseApiError } from '../../services/apiError';

const PAGE_SIZE = 20;

function dateToTimeString(date: Date | null): string | undefined {
    if (!date) return undefined;
    return toISOString(date);
}

function parseTimeString(value?: string): Date | null {
    if (!value) return null;
    // Parse components manually to avoid UTC interpretation.
    // Backend sends LocalDateTime without offset (e.g. "2026-08-11T10:00:00").
    const [datePart, timePart] = value.split('T');
    if (!datePart || !timePart) return new Date(value);
    const [year, month, day] = datePart.split('-').map(Number);
    const [hours, minutes, seconds] = timePart.replace('Z', '').split(':').map(Number);
    return new Date(year, month - 1, day, hours, minutes, seconds || 0);
}

export default function SurgeryPartView() {
    const [searchParams] = useSearchParams();
    const toastRef = useRef<Toast>(null);
    // El tema oculta el detalle de los avisos: el texto completo va en el título (summary).
    const showToast = (severity: 'success' | 'info' | 'warn' | 'error', text: string, life: number = 4000) => {
        toastRef.current?.show({ severity, summary: text, life });
    };

    const siteID = searchParams.get('siteID');
    const userID = searchParams.get('userID');
    const sessionID = searchParams.get('sessionID');
    const surgeryAppointmentIDParam = searchParams.get('surgeryAppointmentID');
    const surgeryIDParam = searchParams.get('surgeryID');

    const surgeryAppointmentId = surgeryAppointmentIDParam ? Number(surgeryAppointmentIDParam) : 0;
    const surgeryId = surgeryIDParam ? Number(surgeryIDParam) : undefined;

    // Mobile detection for Calendar touchUI and collapsible time fields
    const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
    const [timeFieldsExpanded, setTimeFieldsExpanded] = useState(() => window.innerWidth > 768);
    useEffect(() => {
        const onResize = () => {
            const mobile = window.innerWidth <= 768;
            setIsMobile(mobile);
            if (!mobile) setTimeFieldsExpanded(true);
        };
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    // Loading state
    const [loading, setLoading] = useState<boolean>(true);

    // Patient info
    const [patientData, setPatientData] = useState<Partial<SurgeryPartDTO>>({});

    // Time fields
    const [arrivalTime, setArrivalTime] = useState<Date | null>(null);
    const [surgeryStartTime, setSurgeryStartTime] = useState<Date | null>(null);
    const [surgeryEndTime, setSurgeryEndTime] = useState<Date | null>(null);
    const [egressTime, setEgressTime] = useState<Date | null>(null);

    // Sector
    const [selectedSector, setSelectedSector] = useState<ComboItem | null>(null);

    // Stock items tab
    const [stockItems, setStockItems] = useState<SurgeryStockItemDTO[]>([]);
    // Cantidades ya descontadas por este parte al abrirlo: al editar, no cuentan contra la existencia del sector
    const originalStockQty = useRef<Map<number, number>>(new Map());
    const [supplyQuery, setSupplyQuery] = useState<string>('');
    const [supplySuggestions, setSupplySuggestions] = useState<SurgeryStockItemComboDTO[]>([]);
    const [selectedSupply, setSelectedSupply] = useState<SurgeryStockItemComboDTO | null>(null);
    const [supplyQty, setSupplyQty] = useState<number>(1);
    const [kitMissingModalVisible, setKitMissingModalVisible] = useState<boolean>(false);
    const [kitMissingItems, setKitMissingItems] = useState<SurgeryKitMissingItemDTO[]>([]);
    const [pendingKitComponents, setPendingKitComponents] = useState<SurgeryStockItemDTO[]>([]);

    // Professionals tab
    const [professionals, setProfessionals] = useState<SurgeryProfessionalDTO[]>([]);
    const [professionalRoles, setProfessionalRoles] = useState<ComboItem[]>([]);
    const [professionalQuery, setProfessionalQuery] = useState<string>('');
    const [professionalSuggestions, setProfessionalSuggestions] = useState<ComboItem[]>([]);
    const [selectedProfessional, setSelectedProfessional] = useState<ComboItem | null>(null);
    const [selectedRole, setSelectedRole] = useState<ComboItem | null>(null);

    // Practices tab
    const [practices, setPractices] = useState<SurgeryPracticeCodeDTO[]>([]);
    const [nomenclatorQuery, setNomenclatorQuery] = useState<string>('');
    const [nomenclatorSuggestions, setNomenclatorSuggestions] = useState<ComboItem[]>([]);
    const [selectedNomenclator, setSelectedNomenclator] = useState<ComboItem | null>(null);

    // Other data tab
    const [anesthesiaLegacyFormat, setAnesthesiaLegacyFormat] = useState<boolean>(false);
    const [legacyAnesthesiaOptions, setLegacyAnesthesiaOptions] = useState<ComboItem[]>([]);
    const [selectedLegacyAnesthesiaIds, setSelectedLegacyAnesthesiaIds] = useState<number[]>([]);
    const [anesthesiaTypeOptions, setAnesthesiaTypeOptions] = useState<ComboItem[]>([]);
    const [anesthesiaTechniqueOptions, setAnesthesiaTechniqueOptions] = useState<ComboItem[]>([]);
    const [anesthesiaProcedureOptions, setAnesthesiaProcedureOptions] = useState<ComboItem[]>([]);
    const [surgicalRiskOptions, setSurgicalRiskOptions] = useState<ComboItem[]>([]);
    const [selectedAnesthesiaType, setSelectedAnesthesiaType] = useState<ComboItem | null>(null);
    const [selectedAnesthesiaTechnique, setSelectedAnesthesiaTechnique] = useState<ComboItem | null>(null);
    const [selectedAnesthesiaProcedureIds, setSelectedAnesthesiaProcedureIds] = useState<number[]>([]);
    const [anesthesiaProceduresOtherText, setAnesthesiaProceduresOtherText] = useState<string>('');
    const [selectedRisk, setSelectedRisk] = useState<ComboItem | null>(null);
    const [laboratory, setLaboratory] = useState<boolean>(false);
    const [rx, setRx] = useState<boolean>(false);
    const [hemotherapy, setHemotherapy] = useState<boolean>(false);
    const [pathologyAnatomy, setPathologyAnatomy] = useState<boolean>(false);
    const [pathologyQty, setPathologyQty] = useState<number>(1);

    // Saving state
    const [saving, setSaving] = useState<boolean>(false);

    useEffect(() => {
        const initLoad = async () => {
            setLoading(true);
            try {
                const [roles, legacyAnesthesia, anesthesiaTypes, anesthesiaProcedures, surgicalRisk] = await Promise.all([
                    getProfessionalRoles(),
                    getLegacyAnesthesiaOptions(),
                    getAnesthesiaTypeOptions(),
                    getAnesthesiaProcedureOptions(),
                    getSurgicalRiskOptions(),
                ]);
                setProfessionalRoles(roles);
                setLegacyAnesthesiaOptions(legacyAnesthesia);
                setAnesthesiaTypeOptions(anesthesiaTypes);
                setAnesthesiaProcedureOptions(anesthesiaProcedures);
                setSurgicalRiskOptions(surgicalRisk);

                if (surgeryAppointmentId) {
                    const part = await getSurgeryPart(surgeryAppointmentId, surgeryId);
                    if (part) {
                        setPatientData({
                            patientName: part.patientName,
                            patientAge: part.patientAge,
                            patientGender: part.patientGender,
                            patientDni: part.patientDni,
                            patientClinicHistoryId: part.patientClinicHistoryId,
                            coverageName: part.coverageName,
                            coveragePlan: part.coveragePlan,
                            healthEntityId: part.healthEntityId,
                            surgeryDate: part.surgeryDate,
                            beneficiaryNumber: part.beneficiaryNumber,
                        });
                        setArrivalTime(parseTimeString(part.arrivalTime));
                        setSurgeryStartTime(parseTimeString(part.surgeryStartTime));
                        setSurgeryEndTime(parseTimeString(part.surgeryEndTime));
                        setEgressTime(parseTimeString(part.egressTime));
                        setStockItems(part.stockItems ?? []);
                        originalStockQty.current = (part.stockItems ?? []).reduce(
                            (acc, item) => acc.set(item.stockItemPresentationId, (acc.get(item.stockItemPresentationId) ?? 0) + item.quantity),
                            new Map<number, number>()
                        );
                        setProfessionals(part.professionals ?? []);
                        setPractices(part.practiceCodes ?? []);
                        setAnesthesiaLegacyFormat(part.anesthesiaLegacyFormat ?? false);
                        setSelectedLegacyAnesthesiaIds(part.legacyAnesthesiaTypeIds ?? []);
                        setSelectedAnesthesiaProcedureIds(part.anesthesiaProcedureIds ?? []);
                        setAnesthesiaProceduresOtherText(part.anesthesiaProceduresOtherText ?? '');
                        setLaboratory(part.laboratory ?? false);
                        setRx(part.rx ?? false);
                        setHemotherapy(part.hemotherapy ?? false);
                        setPathologyAnatomy(part.pathologyAnatomy ?? false);
                        setPathologyQty(part.pathologyQty ?? 1);
                        // El banco de insumos lo decide el servidor (quirófano del turno): solo se muestra
                        if (part.sectorId) {
                            setSelectedSector({ id: part.sectorId, name: part.sectorName ?? '' });
                        }
                        if (part.operativeRiskId) {
                            const riskMatch = surgicalRisk.find(r => r.id === part.operativeRiskId) ?? null;
                            setSelectedRisk(riskMatch);
                        }
                        if (part.anesthesiaTypeId) {
                            const typeMatch = anesthesiaTypes.find(t => t.id === part.anesthesiaTypeId) ?? null;
                            setSelectedAnesthesiaType(typeMatch);
                            if (typeMatch) {
                                const techniques = await getAnesthesiaTechniqueOptions(typeMatch.id);
                                setAnesthesiaTechniqueOptions(techniques);
                                if (part.anesthesiaTechniqueId) {
                                    const techniqueMatch = techniques.find(t => t.id === part.anesthesiaTechniqueId) ?? null;
                                    setSelectedAnesthesiaTechnique(techniqueMatch);
                                }
                            }
                        }
                    }
                }
            } catch (err) {
                console.error(err);
                showToast('error', 'Error al cargar los datos iniciales.', 4000);
            } finally {
                setLoading(false);
            }
        };
        initLoad();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // AutoComplete: supplies
    const searchSupplies = async (event: AutoCompleteCompleteEvent) => {
        const query = event.query;
        if (query.length < 3) {
            setSupplySuggestions([]);
            return;
        }
        const results = await getStockItems(surgeryAppointmentId, query, 0, PAGE_SIZE);
        setSupplySuggestions(results);
    };

    const mergeStockItems = (current: SurgeryStockItemDTO[], incoming: SurgeryStockItemDTO[]): SurgeryStockItemDTO[] => {
        const merged = [...current];
        incoming.forEach(item => {
            const existing = merged.findIndex(s => s.stockItemPresentationId === item.stockItemPresentationId);
            if (existing >= 0) {
                merged[existing] = {
                    ...merged[existing],
                    quantity: merged[existing].quantity + item.quantity,
                };
            } else {
                merged.push(item);
            }
        });
        return merged;
    };

    // AutoComplete: professionals
    const searchProfessionals = async (event: AutoCompleteCompleteEvent) => {
        const query = event.query;
        if (query.length < 3) {
            setProfessionalSuggestions([]);
            return;
        }
        const results = await getProfessionals(query, 0, PAGE_SIZE);
        setProfessionalSuggestions(results);
    };

    // AutoComplete: nomenclators
    const searchNomenclators = async (event: AutoCompleteCompleteEvent) => {
        const query = event.query;
        if (query.length < 3) {
            setNomenclatorSuggestions([]);
            return;
        }
        const results = await getNomenclatorsForHealthEntity(
            query,
            0,
            PAGE_SIZE,
            patientData.healthEntityId,
            patientData.surgeryDate,
        );
        setNomenclatorSuggestions(results);
    };

    /**
     * Control de saldo, igual al de la ventana anterior del parte: no se puede cargar más de lo que el sector tiene.
     * Compara lo ya cargado en el parte más lo que se suma contra la existencia del sector (más lo que este mismo
     * parte ya descontó al abrirlo, si se está editando). Avisa y devuelve false si no alcanza.
     */
    const hasEnoughStock = async (itemId: number, description: string, additionalQty: number): Promise<boolean> => {
        if (!selectedSector) {
            showToast('warn', 'El quirófano no tiene un banco de insumos configurado.', 4000);
            return false;
        }
        const stockBalance = await getStockBalance(surgeryAppointmentId, itemId);
        if (stockBalance !== null && !stockBalance.controlActive) {
            return true; // el sitio tiene desactivado el control de stock
        }
        const balance = stockBalance?.balance ?? null;
        if (balance === null) {
            showToast('warn', 'No se pudo consultar el stock del insumo. Intente nuevamente.', 4000);
            return false;
        }
        const available = balance + (originalStockQty.current.get(itemId) ?? 0);
        const inPart = stockItems
            .filter(item => item.stockItemPresentationId === itemId)
            .reduce((sum, item) => sum + item.quantity, 0);
        if (inPart + additionalQty > available) {
            // El banco tiene una existencia (la que ven todos los partes). De ella, este parte ya tiene cargado sin
            // guardar lo que excede a lo que ya había descontado al abrirse (o liberó lo que quitó).
            const bankStock = Math.max(balance, 0);
            const pending = inPart - (originalStockQty.current.get(itemId) ?? 0);
            const detail = pending > 0
                ? `, de los cuales este parte ya tiene ${pending} cargados sin guardar`
                : pending < 0
                    ? `, más ${-pending} que quitaste de este parte`
                    : '';
            showToast(
                'warn',
                `${description}: querés agregar ${additionalQty} y en el banco hay ${bankStock}${detail}. Por favor, contactar con farmacia.`,
                8000
            );
            return false;
        }
        return true;
    };

    // Add supply
    const handleAddSupply = async () => {
        if (!selectedSupply) {
            showToast('warn', 'Seleccione un insumo.', 3000);
            return;
        }
        if (!supplyQty || supplyQty <= 0) {
            showToast('warn', 'La cantidad debe ser mayor a 0.', 3000);
            return;
        }
        if (selectedSupply.kit) {
            if (!selectedSector) {
                showToast('warn', 'El quirófano no tiene un banco de insumos configurado.', 4000);
                return;
            }

            const validation = await validateKit(selectedSupply.id, supplyQty, surgeryAppointmentId);
            if (!validation) {
                showToast('warn', 'No se pudo agregar el kit.', 4000);
                return;
            }

            if (!validation.valid && (validation.missingComponents?.length ?? 0) > 0) {
                setPendingKitComponents(validation.components ?? []);
                setKitMissingItems(validation.missingComponents ?? []);
                setKitMissingModalVisible(true);
                return;
            }

            setStockItems(prev => mergeStockItems(prev, validation.components ?? []));
        } else {
            if (!(await hasEnoughStock(selectedSupply.id, selectedSupply.name, supplyQty))) {
                return;
            }
            setStockItems(prev =>
                mergeStockItems(prev, [
                    {
                        stockItemPresentationId: selectedSupply.id,
                        description: selectedSupply.name,
                        quantity: supplyQty,
                    },
                ])
            );
        }
        setSelectedSupply(null);
        setSupplyQuery('');
        setSupplyQty(1);
    };

    const applyPendingKit = () => {
        setStockItems(prev => mergeStockItems(prev, pendingKitComponents));
        setPendingKitComponents([]);
        setKitMissingItems([]);
        setKitMissingModalVisible(false);
        setSelectedSupply(null);
        setSupplyQuery('');
        setSupplyQty(1);
    };

    const closeKitMissingModal = () => {
        setPendingKitComponents([]);
        setKitMissingItems([]);
        setKitMissingModalVisible(false);
    };

    const handleClearSupply = () => {
        closeKitMissingModal();
        setSelectedSupply(null);
        setSupplyQuery('');
        setSupplyQty(1);
    };

    const handleRemoveSupply = (idx: number) => {
        setStockItems(prev => prev.filter((_, i) => i !== idx));
    };

    const handleSupplyQtyChange = async (idx: number, delta: number) => {
        const target = stockItems[idx];
        if (delta > 0 && target && !(await hasEnoughStock(target.stockItemPresentationId, target.description, delta))) {
            return;
        }
        setStockItems(prev =>
            prev.map((item, i) => {
                if (i !== idx) return item;
                const newQty = item.quantity + delta;
                return { ...item, quantity: newQty > 0 ? newQty : 1 };
            })
        );
    };

    // Add professional
    const ROLE_CIRUJANO_PRINCIPAL = 'cirujano principal';
    const handleAddProfessional = () => {
        if (!selectedProfessional) {
            showToast('warn', 'Seleccione un profesional.', 3000);
            return;
        }
        if (!selectedRole) {
            showToast('warn', 'Seleccione un rol.', 3000);
            return;
        }
        const duplicate = professionals.find(p => p.professionalId === selectedProfessional.id);
        if (duplicate) {
            showToast('warn', 'El profesional ya fue agregado.', 3000);
            return;
        }
        if (selectedRole.name.toLowerCase() === ROLE_CIRUJANO_PRINCIPAL) {
            const hasMain = professionals.some(p => p.roleName.toLowerCase() === ROLE_CIRUJANO_PRINCIPAL);
            if (hasMain) {
                showToast('warn', 'Ya existe un cirujano principal.', 3000);
                return;
            }
        }
        setProfessionals(prev => [
            ...prev,
            {
                professionalId: selectedProfessional.id,
                professionalName: selectedProfessional.name,
                roleId: selectedRole.id,
                roleName: selectedRole.name,
                order: prev.length + 1,
            },
        ]);
        setSelectedProfessional(null);
        setProfessionalQuery('');
        setSelectedRole(null);
    };

    const handleRemoveProfessional = (idx: number) => {
        setProfessionals(prev => prev.filter((_, i) => i !== idx));
    };

    // Add practice
    const handleAddPractice = () => {
        if (!selectedNomenclator) {
            showToast('warn', 'Seleccione una práctica.', 3000);
            return;
        }
        const duplicate = practices.find(p => p.nomenclatorId === selectedNomenclator.id);
        if (duplicate) {
            showToast('warn', 'La práctica ya fue agregada.', 3000);
            return;
        }
        const parts = selectedNomenclator.name.split(' - ');
        const code = parts[0] ?? '';
        const description = parts.slice(1).join(' - ') || selectedNomenclator.name;
        setPractices(prev => [
            ...prev,
            {
                nomenclatorId: selectedNomenclator.id,
                code,
                description,
            },
        ]);
        setSelectedNomenclator(null);
        setNomenclatorQuery('');
    };

    const handleRemovePractice = (idx: number) => {
        setPractices(prev => prev.filter((_, i) => i !== idx));
    };

    // Validate dates order
    const validateDates = (): boolean => {
        const times = [arrivalTime, surgeryStartTime, surgeryEndTime, egressTime];
        const labels = ['Hora de ingreso', 'Inicio cirugía', 'Fin cirugía', 'Hora de egreso'];
        for (let i = 0; i < times.length - 1; i++) {
            const a = times[i];
            const b = times[i + 1];
            if (a && b && a > b) {
                showToast('error', `"${labels[i]}" debe ser anterior a "${labels[i + 1]}".`, 4000);
                return false;
            }
        }
        return true;
    };

    const otherProcedureOption = anesthesiaProcedureOptions.find(o => o.name === 'Otro') ?? null;
    const isOtherProcedureSelected = otherProcedureOption !== null && selectedAnesthesiaProcedureIds.includes(otherProcedureOption.id);

    const handleAnesthesiaProceduresChange = (ids: number[]) => {
        setSelectedAnesthesiaProcedureIds(ids);
        if (!otherProcedureOption || !ids.includes(otherProcedureOption.id)) {
            setAnesthesiaProceduresOtherText('');
        }
    };

    const handleAnesthesiaTypeChange = async (value: ComboItem | null) => {
        setSelectedAnesthesiaType(value);
        setSelectedAnesthesiaTechnique(null);
        if (!value) {
            setAnesthesiaTechniqueOptions([]);
            return;
        }
        const techniques = await getAnesthesiaTechniqueOptions(value.id);
        setAnesthesiaTechniqueOptions(techniques);
    };

    // Save
    const handleSave = async () => {
        if (!validateDates()) return;
        if (stockItems.length > 0 && !selectedSector) {
            showToast('warn', 'El quirófano no tiene un banco de insumos configurado.', 4000);
            return;
        }
        setSaving(true);
        try {
            const payload: SurgeryPartDTO = {
                surgeryId,
                surgeryAppointmentId,
                arrivalTime: dateToTimeString(arrivalTime),
                surgeryStartTime: dateToTimeString(surgeryStartTime),
                surgeryEndTime: dateToTimeString(surgeryEndTime),
                egressTime: dateToTimeString(egressTime),
                stockItems,
                professionals,
                practiceCodes: practices,
                operativeRiskId: selectedRisk?.id,
                anesthesiaLegacyFormat,
                legacyAnesthesiaTypeIds: anesthesiaLegacyFormat ? selectedLegacyAnesthesiaIds : undefined,
                anesthesiaTypeId: anesthesiaLegacyFormat ? undefined : selectedAnesthesiaType?.id,
                anesthesiaTechniqueId: anesthesiaLegacyFormat ? undefined : selectedAnesthesiaTechnique?.id,
                anesthesiaProcedureIds: anesthesiaLegacyFormat ? undefined : selectedAnesthesiaProcedureIds,
                anesthesiaProceduresOtherText: (!anesthesiaLegacyFormat && isOtherProcedureSelected) ? anesthesiaProceduresOtherText.trim() : undefined,
                laboratory,
                rx,
                hemotherapy,
                pathologyAnatomy,
                pathologyQty: pathologyAnatomy ? pathologyQty : undefined,
            };
            await saveSurgeryPart(payload);
            showToast('success', 'El parte quirúrgico fue guardado correctamente.', 3000);
            if (sessionID) {
                window.parent.postMessage(
                    { type: 'MEDERE_CLOSE_SURGERY_PART_WINDOW', sessionId: sessionID },
                    window.location.origin
                );
            }
        } catch (err) {
            const apiError = parseApiError(err);
            const insufficientStock = apiError.errorCode === INSUFFICIENT_STOCK;
            showToast(
                insufficientStock ? 'warn' : 'error',
                insufficientStock ? `${apiError.message} El parte no se guardó.` : 'No se pudo guardar el parte quirúrgico.',
                insufficientStock ? 8000 : 4000
            );
        } finally {
            setSaving(false);
        }
    };

    const handleDiscard = () => {
        window.history.back();
    };

    if (loading) {
        return (
            <div className="surgery-part-view surgery-part-view--loading">
                <ProgressSpinner />
            </div>
        );
    }

    return (
        <div className="surgery-part-view">
            <Toast ref={toastRef} />

            <SurgeryKitMissingItemsDialog
                visible={kitMissingModalVisible}
                items={kitMissingItems}
                onHide={closeKitMissingModal}
                onApply={applyPendingKit}
            />
            {/* Patient Header */}
            <div className="patient-header">
                <div className="patient-header__grid">
                    <div className="patient-header__field">
                        <span className="patient-header__label">Paciente</span>
                        <span className="patient-header__value patient-header__value--name">
                            {patientData.patientName ?? '—'}
                        </span>
                    </div>
                    <div className="patient-header__field">
                        <span className="patient-header__label">Edad</span>
                        <span className="patient-header__value">{patientData.patientAge ?? '—'}</span>
                    </div>
                    <div className="patient-header__field">
                        <span className="patient-header__label">Sexo</span>
                        <span className="patient-header__value">{patientData.patientGender ?? '—'}</span>
                    </div>
                    <div className="patient-header__field">
                        <span className="patient-header__label">DNI</span>
                        <span className="patient-header__value">{patientData.patientDni ?? '—'}</span>
                    </div>
                    <div className="patient-header__field">
                        <span className="patient-header__label">HC</span>
                        <span className="patient-header__value">{patientData.patientClinicHistoryId ?? '—'}</span>
                    </div>
                    <div className="patient-header__field">
                        <span className="patient-header__label">Cobertura</span>
                        <span className="patient-header__value">{patientData.coverageName ?? '—'}</span>
                    </div>
                    <div className="patient-header__field">
                        <span className="patient-header__label">Plan</span>
                        <span className="patient-header__value">{patientData.coveragePlan ?? '—'}</span>
                    </div>
                    <div className="patient-header__field">
                        <span className="patient-header__label">N° Beneficiario</span>
                        <span className="patient-header__value">{patientData.beneficiaryNumber ?? '—'}</span>
                    </div>
                </div>
            </div>

            {/* Time Fields */}
            <div className={`time-fields-section ${isMobile ? 'time-fields-section--collapsible' : ''}`}>
                {isMobile && (
                    <button
                        type="button"
                        className="time-fields-section__toggle"
                        onClick={() => setTimeFieldsExpanded(prev => !prev)}
                    >
                        <i className="pi pi-clock" />
                        <span>Horarios</span>
                        <i className={`pi pi-chevron-${timeFieldsExpanded ? 'up' : 'down'}`} />
                    </button>
                )}
                {timeFieldsExpanded && (
                    <div className="time-fields-row">
                        <div className="time-field">
                            <label className="time-field__label">Hora de ingreso</label>
                            <Calendar
                                value={arrivalTime}
                                onChange={e => setArrivalTime(e.value as Date | null)}
                                showTime
                                hourFormat="24"
                                showIcon
                                dateFormat="dd/mm/yy"
                                placeholder="dd/mm/aaaa hh:mm"
                                className="w-full"
                                touchUI={isMobile}
                            />
                        </div>
                        <div className="time-field">
                            <label className="time-field__label">Inicio cirugía</label>
                            <Calendar
                                value={surgeryStartTime}
                                onChange={e => setSurgeryStartTime(e.value as Date | null)}
                                showTime
                                hourFormat="24"
                                showIcon
                                dateFormat="dd/mm/yy"
                                placeholder="dd/mm/aaaa hh:mm"
                                className="w-full"
                                touchUI={isMobile}
                            />
                        </div>
                        <div className="time-field">
                            <label className="time-field__label">Fin cirugía</label>
                            <Calendar
                                value={surgeryEndTime}
                                onChange={e => setSurgeryEndTime(e.value as Date | null)}
                                showTime
                                hourFormat="24"
                                showIcon
                                dateFormat="dd/mm/yy"
                                placeholder="dd/mm/aaaa hh:mm"
                                className="w-full"
                                touchUI={isMobile}
                            />
                        </div>
                        <div className="time-field">
                            <label className="time-field__label">Hora de egreso</label>
                            <Calendar
                                value={egressTime}
                                onChange={e => setEgressTime(e.value as Date | null)}
                                showTime
                                hourFormat="24"
                                showIcon
                                dateFormat="dd/mm/yy"
                                placeholder="dd/mm/aaaa hh:mm"
                                className="w-full"
                                touchUI={isMobile}
                            />
                        </div>
                    </div>
                )}
            </div>

            {/* Tabs */}
            <div className="tabs-section">
                <TabView>

                    {/* Tab Insumos médicos */}
                    <TabPanel header="Insumos médicos" leftIcon="pi pi-box mr-2">
                        <div className="tab-content">
                            <div className="form-field form-field--full">
                                <label className="form-field__label">Sector</label>
                                <InputText
                                    value={selectedSector?.name ?? 'Sin banco de insumos configurado'}
                                    disabled
                                    className="w-full"
                                />
                            </div>
                            <div className="add-item-row">
                                <div className="add-item-row__field add-item-row__field--grow">
                                    <label className="form-field__label">Insumo</label>
                                    <AutoComplete
                                        value={selectedSupply ?? supplyQuery}
                                        suggestions={supplySuggestions}
                                        completeMethod={searchSupplies}
                                        field="name"
                                        onChange={e => {
                                            if (typeof e.value === 'string') {
                                                setSupplyQuery(e.value);
                                                setSelectedSupply(null);
                                            } else {
                                                setSelectedSupply(e.value);
                                                setSupplyQuery('');
                                            }
                                        }}
                                        onSelect={e => setSelectedSupply(e.value)}
                                        placeholder="Buscar insumo (mín. 3 caracteres)"
                                        className="w-full"
                                        minLength={3}
                                    />
                                </div>
                                <div className="add-item-row__field add-item-row__field--qty">
                                    <label className="form-field__label">Cantidad</label>
                                    <InputNumber
                                        value={supplyQty}
                                        onValueChange={e => setSupplyQty(e.value ?? 1)}
                                        min={1}
                                        showButtons
                                        buttonLayout="horizontal"
                                        decrementButtonClassName="p-button-secondary"
                                        incrementButtonClassName="p-button-secondary"
                                        incrementButtonIcon="pi pi-plus"
                                        decrementButtonIcon="pi pi-minus"
                                        className="w-full"
                                    />
                                </div>
                                <div className="add-item-row__actions">
                                    <label className="form-field__label">&nbsp;</label>
                                    <div>
                                        <Button
                                            icon="pi pi-plus"
                                            label="Agregar"
                                            onClick={handleAddSupply}
                                            className="p-button-primary"
                                        />
                                        <Button
                                            icon="pi pi-eraser"
                                            onClick={handleClearSupply}
                                            className="p-button-secondary p-button-outlined"
                                            tooltip="Limpiar"
                                            tooltipOptions={{ position: 'top' }}
                                        />
                                    </div>
                                </div>
                            </div>
                            <DataTable
                                value={stockItems}
                                emptyMessage="No se han agregado insumos."
                                size="small"
                                className="surgery-table"
                                scrollable
                                scrollHeight="300px"
                            >
                                <Column field="description" header="Descripción" style={{ minWidth: '280px' }} />
                                <Column
                                    field="quantity"
                                    header="Cantidad"
                                    style={{ width: '150px', textAlign: 'center' }}
                                    body={(row: SurgeryStockItemDTO, opts) => (
                                        <div className="qty-control">
                                            <Button
                                                icon="pi pi-minus"
                                                className="p-button-rounded p-button-secondary p-button-sm p-button-text"
                                                onClick={() => handleSupplyQtyChange(opts.rowIndex, -1)}
                                            />
                                            <span className="qty-control__value">{row.quantity}</span>
                                            <Button
                                                icon="pi pi-plus"
                                                className="p-button-rounded p-button-secondary p-button-sm p-button-text"
                                                onClick={() => handleSupplyQtyChange(opts.rowIndex, 1)}
                                            />
                                        </div>
                                    )}
                                />
                                <Column
                                    header=""
                                    style={{ width: '60px', textAlign: 'center' }}
                                    body={(_row, opts) => (
                                        <Button
                                            icon="pi pi-trash"
                                            className="p-button-rounded p-button-danger p-button-text p-button-sm"
                                            onClick={() => handleRemoveSupply(opts.rowIndex)}
                                            tooltip="Eliminar"
                                            tooltipOptions={{ position: 'top' }}
                                        />
                                    )}
                                />
                            </DataTable>
                        </div>
                    </TabPanel>

                    {/* Tab Profesionales */}
                    <TabPanel header="Profesionales" leftIcon="pi pi-user mr-2">
                        <div className="tab-content">
                            <div className="add-item-row">
                                <div className="add-item-row__field add-item-row__field--grow">
                                    <label className="form-field__label">Profesional</label>
                                    <AutoComplete
                                        value={selectedProfessional ?? professionalQuery}
                                        suggestions={professionalSuggestions}
                                        completeMethod={searchProfessionals}
                                        field="name"
                                        onChange={e => {
                                            if (typeof e.value === 'string') {
                                                setProfessionalQuery(e.value);
                                                setSelectedProfessional(null);
                                            } else {
                                                setSelectedProfessional(e.value);
                                                setProfessionalQuery('');
                                            }
                                        }}
                                        onSelect={e => setSelectedProfessional(e.value)}
                                        placeholder="Buscar profesional (mín. 3 caracteres)"
                                        className="w-full"
                                        minLength={3}
                                    />
                                </div>
                                <div className="add-item-row__field add-item-row__field--role">
                                    <label className="form-field__label">Rol</label>
                                    <Dropdown
                                        value={selectedRole}
                                        options={professionalRoles}
                                        optionLabel="name"
                                        onChange={e => setSelectedRole(e.value)}
                                        placeholder="Seleccione un rol"
                                        className="w-full"
                                    />
                                </div>
                                <div className="add-item-row__actions">
                                    <label className="form-field__label">&nbsp;</label>
                                    <Button
                                        label="Agregar"
                                        icon="pi pi-plus"
                                        onClick={handleAddProfessional}
                                        className="p-button-primary"
                                    />
                                </div>
                            </div>
                            <DataTable
                                value={professionals}
                                emptyMessage="No se han agregado profesionales."
                                size="small"
                                className="surgery-table"
                                scrollable
                                scrollHeight="300px"
                            >
                                <Column field="professionalName" header="Profesional" style={{ minWidth: '220px' }} />
                                <Column field="roleName" header="Rol" style={{ minWidth: '180px' }} />
                                <Column
                                    header=""
                                    style={{ width: '60px', textAlign: 'center' }}
                                    body={(_row, opts) => (
                                        <Button
                                            icon="pi pi-trash"
                                            className="p-button-rounded p-button-danger p-button-text p-button-sm"
                                            onClick={() => handleRemoveProfessional(opts.rowIndex)}
                                            tooltip="Eliminar"
                                            tooltipOptions={{ position: 'top' }}
                                        />
                                    )}
                                />
                            </DataTable>
                        </div>
                    </TabPanel>

                    {/* Tab Prácticas */}
                    <TabPanel header="Prácticas" leftIcon="pi pi-list mr-2">
                        <div className="tab-content">
                            <div className="add-item-row">
                                <div className="add-item-row__field add-item-row__field--grow">
                                    <label className="form-field__label">Práctica / Nomenclador</label>
                                    <AutoComplete
                                        value={selectedNomenclator ?? nomenclatorQuery}
                                        suggestions={nomenclatorSuggestions}
                                        completeMethod={searchNomenclators}
                                        field="name"
                                        onChange={e => {
                                            if (typeof e.value === 'string') {
                                                setNomenclatorQuery(e.value);
                                                setSelectedNomenclator(null);
                                            } else {
                                                setSelectedNomenclator(e.value);
                                                setNomenclatorQuery('');
                                            }
                                        }}
                                        onSelect={e => setSelectedNomenclator(e.value)}
                                        placeholder="Buscar práctica (mín. 3 caracteres)"
                                        className="w-full"
                                        minLength={3}
                                    />
                                </div>
                                <div className="add-item-row__actions">
                                    <label className="form-field__label">&nbsp;</label>
                                    <Button
                                        label="Agregar"
                                        icon="pi pi-plus"
                                        onClick={handleAddPractice}
                                        className="p-button-primary"
                                    />
                                </div>
                            </div>
                            <DataTable
                                value={practices}
                                emptyMessage="No se han agregado prácticas."
                                size="small"
                                className="surgery-table"
                                scrollable
                                scrollHeight="300px"
                            >
                                <Column field="code" header="Código" style={{ width: '120px' }} />
                                <Column field="description" header="Descripción" style={{ minWidth: '280px' }} />
                                <Column
                                    header=""
                                    style={{ width: '60px', textAlign: 'center' }}
                                    body={(_row, opts) => (
                                        <Button
                                            icon="pi pi-trash"
                                            className="p-button-rounded p-button-danger p-button-text p-button-sm"
                                            onClick={() => handleRemovePractice(opts.rowIndex)}
                                            tooltip="Eliminar"
                                            tooltipOptions={{ position: 'top' }}
                                        />
                                    )}
                                />
                            </DataTable>
                        </div>
                    </TabPanel>

                    {/* Tab Otros datos */}
                    <TabPanel header="Otros datos" leftIcon="pi pi-info-circle mr-2">
                        <div className="tab-content tab-content--other">
                            {anesthesiaLegacyFormat ? (
                                <div className="other-data-grid">
                                    <div className="form-field">
                                        <label className="form-field__label">Tipos de anestesia</label>
                                        <MultiSelect
                                            value={selectedLegacyAnesthesiaIds}
                                            options={legacyAnesthesiaOptions}
                                            optionLabel="name"
                                            optionValue="id"
                                            onChange={e => setSelectedLegacyAnesthesiaIds(e.value)}
                                            placeholder="Seleccione uno o más tipos"
                                            className="w-full"
                                            display="chip"
                                        />
                                    </div>
                                </div>
                            ) : (
                                <div className="anesthesia-row">
                                    <div className="form-field">
                                        <label className="form-field__label">Tipo de anestesia</label>
                                        <Dropdown
                                            value={selectedAnesthesiaType}
                                            options={anesthesiaTypeOptions}
                                            optionLabel="name"
                                            onChange={e => { void handleAnesthesiaTypeChange(e.value); }}
                                            placeholder="Seleccione un tipo"
                                            className="w-full"
                                            showClear
                                        />
                                    </div>
                                    <div className="form-field">
                                        <label className="form-field__label">Técnica o vía</label>
                                        <Dropdown
                                            value={selectedAnesthesiaTechnique}
                                            options={anesthesiaTechniqueOptions}
                                            optionLabel="name"
                                            onChange={e => setSelectedAnesthesiaTechnique(e.value)}
                                            placeholder="Seleccione una técnica"
                                            className="w-full"
                                            showClear
                                            disabled={!selectedAnesthesiaType}
                                        />
                                    </div>
                                    <div className="form-field">
                                        <label className="form-field__label">Procedimientos/dispositivos</label>
                                        <MultiSelect
                                            value={selectedAnesthesiaProcedureIds}
                                            options={anesthesiaProcedureOptions}
                                            optionLabel="name"
                                            optionValue="id"
                                            onChange={e => handleAnesthesiaProceduresChange(e.value)}
                                            placeholder="Seleccione uno o más procedimientos"
                                            className="w-full"
                                            display="chip"
                                        />
                                        {isOtherProcedureSelected && (
                                            <InputText
                                                value={anesthesiaProceduresOtherText}
                                                onChange={e => setAnesthesiaProceduresOtherText(e.target.value)}
                                                placeholder="Especifique &quot;Otro&quot;"
                                                className="w-full"
                                            />
                                        )}
                                    </div>
                                </div>
                            )}
                            <div className="other-data-grid">
                                <div className="form-field">
                                    <label className="form-field__label">Riesgo quirúrgico</label>
                                    <Dropdown
                                        value={selectedRisk}
                                        options={surgicalRiskOptions}
                                        optionLabel="name"
                                        onChange={e => setSelectedRisk(e.value)}
                                        placeholder="Seleccione riesgo"
                                        className="w-full"
                                        showClear
                                    />
                                </div>
                            </div>
                            <div className="checkboxes-section">
                                <span className="checkboxes-section__title">Servicios complementarios</span>
                                <div className="checkboxes-grid">
                                    <div className="checkbox-item">
                                        <Checkbox
                                            inputId="chk-lab"
                                            checked={laboratory}
                                            onChange={e => setLaboratory(e.checked ?? false)}
                                        />
                                        <label htmlFor="chk-lab" className="checkbox-item__label">Laboratorio</label>
                                    </div>
                                    <div className="checkbox-item">
                                        <Checkbox
                                            inputId="chk-rx"
                                            checked={rx}
                                            onChange={e => setRx(e.checked ?? false)}
                                        />
                                        <label htmlFor="chk-rx" className="checkbox-item__label">Rx</label>
                                    </div>
                                    <div className="checkbox-item">
                                        <Checkbox
                                            inputId="chk-hemo"
                                            checked={hemotherapy}
                                            onChange={e => setHemotherapy(e.checked ?? false)}
                                        />
                                        <label htmlFor="chk-hemo" className="checkbox-item__label">Hemoterapia</label>
                                    </div>
                                    <div className="checkbox-item">
                                        <Checkbox
                                            inputId="chk-path"
                                            checked={pathologyAnatomy}
                                            onChange={e => setPathologyAnatomy(e.checked ?? false)}
                                        />
                                        <label htmlFor="chk-path" className="checkbox-item__label">Patología / Anatomía</label>
                                    </div>
                                    {pathologyAnatomy && (
                                        <div className="checkbox-item checkbox-item--qty">
                                            <label className="form-field__label">Cantidad de muestras</label>
                                            <InputNumber
                                                value={pathologyQty}
                                                onValueChange={e => setPathologyQty(e.value ?? 1)}
                                                min={1}
                                                showButtons
                                                buttonLayout="horizontal"
                                                decrementButtonClassName="p-button-secondary"
                                                incrementButtonClassName="p-button-secondary"
                                                incrementButtonIcon="pi pi-plus"
                                                decrementButtonIcon="pi pi-minus"
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </TabPanel>
                </TabView>
            </div>

            {/* Footer */}
            <div className="footer-actions">
                <Button
                    label="Descartar cambios"
                    icon="pi pi-times"
                    className="p-button-outlined p-button-secondary"
                    onClick={handleDiscard}
                    disabled={saving}
                />
                <Button
                    label="Confirmar Parte"
                    icon="pi pi-check"
                    className="p-button-primary footer-actions__confirm-btn"
                    onClick={handleSave}
                    loading={saving}
                />
            </div>
        </div>
    );
}

